import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

export type GenieMoodType = 'default' | 'security' | 'study' | 'creative' | 'business';

interface MoodConfig {
  name: string;
  accentHue: number;
  bgGradient: string;
  motionIntensity: 'low' | 'medium' | 'high';
  particleCount: number;
}

export const MOOD_CONFIGS: Record<GenieMoodType, MoodConfig> = {
  default: {
    name: 'Default',
    accentHue: 250, // Primary purple
    bgGradient: 'from-primary/5 via-transparent to-primary/5',
    motionIntensity: 'medium',
    particleCount: 30,
  },
  security: {
    name: 'Security',
    accentHue: 200, // Muted blue
    bgGradient: 'from-blue-500/5 via-transparent to-slate-500/5',
    motionIntensity: 'low',
    particleCount: 15,
  },
  study: {
    name: 'Study',
    accentHue: 45, // Warm yellow
    bgGradient: 'from-amber-500/5 via-transparent to-orange-500/5',
    motionIntensity: 'low',
    particleCount: 20,
  },
  creative: {
    name: 'Creative',
    accentHue: 280, // Vibrant purple
    bgGradient: 'from-purple-500/5 via-pink-500/5 to-violet-500/5',
    motionIntensity: 'high',
    particleCount: 50,
  },
  business: {
    name: 'Business',
    accentHue: 160, // Professional green
    bgGradient: 'from-emerald-500/5 via-transparent to-teal-500/5',
    motionIntensity: 'low',
    particleCount: 10,
  },
};

// Route-to-mood mapping
const ROUTE_MOOD_MAP: Record<string, GenieMoodType> = {
  '/contract-analyzer': 'security',
  '/ciso-knowledge-base': 'security',
  '/admin/audit': 'security',
  '/college-ready': 'study',
  '/prompt-cube': 'study',
  '/pdf-storybook-architect': 'creative',
  '/ai-image-generator': 'creative',
  '/pic-editor': 'creative',
  '/tools/video': 'creative',
  '/chatbot-builder': 'business',
  '/pipelines': 'business',
  '/geniebot-material': 'business',
};

interface GenieMoodContextValue {
  currentMood: GenieMoodType;
  moodConfig: MoodConfig;
  setMoodOverride: (mood: GenieMoodType | null) => void;
  moodOverride: GenieMoodType | null;
  isAutoDetected: boolean;
}

const GenieMoodContext = createContext<GenieMoodContextValue | undefined>(undefined);

interface GenieMoodProviderProps {
  children: ReactNode;
}

export function GenieMoodProvider({ children }: GenieMoodProviderProps) {
  const location = useLocation();
  const { user } = useAuth();
  const [moodOverride, setMoodOverrideState] = useState<GenieMoodType | null>(null);
  const [dbOverride, setDbOverride] = useState<GenieMoodType | null>(null);

  // Load user's mood override from database
  useEffect(() => {
    const loadMoodOverride = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('genie_mood_override')
          .eq('id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading mood override:', error);
          return;
        }

        if (data?.genie_mood_override) {
          setDbOverride(data.genie_mood_override as GenieMoodType);
        }
      } catch (error) {
        console.error('Error loading mood override:', error);
      }
    };

    loadMoodOverride();
  }, [user]);

  // Save mood override to database
  const setMoodOverride = useCallback(async (mood: GenieMoodType | null) => {
    setMoodOverrideState(mood);

    if (user) {
      try {
        await supabase
          .from('profiles')
          .upsert({
            id: user.id,
            genie_mood_override: mood,
          }, { onConflict: 'id' });
      } catch (error) {
        console.error('Error saving mood override:', error);
      }
    }
  }, [user]);

  // Determine current mood based on route or override
  const getCurrentMood = useCallback((): GenieMoodType => {
    // User's explicit override takes priority
    if (moodOverride) return moodOverride;
    if (dbOverride) return dbOverride;

    // Check route-based detection
    const pathname = location.pathname;
    for (const [route, mood] of Object.entries(ROUTE_MOOD_MAP)) {
      if (pathname.startsWith(route)) {
        return mood;
      }
    }

    return 'default';
  }, [location.pathname, moodOverride, dbOverride]);

  const currentMood = getCurrentMood();
  const moodConfig = MOOD_CONFIGS[currentMood];
  const isAutoDetected = !moodOverride && !dbOverride;

  // Apply mood CSS variables
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--genie-mood-hue', String(moodConfig.accentHue));
    root.style.setProperty('--genie-mood-particles', String(moodConfig.particleCount));
    
    // Set motion intensity class
    root.classList.remove('motion-low', 'motion-medium', 'motion-high');
    root.classList.add(`motion-${moodConfig.motionIntensity}`);
  }, [moodConfig]);

  return (
    <GenieMoodContext.Provider
      value={{
        currentMood,
        moodConfig,
        setMoodOverride,
        moodOverride: moodOverride || dbOverride,
        isAutoDetected,
      }}
    >
      {children}
    </GenieMoodContext.Provider>
  );
}

export function useGenieMood() {
  const context = useContext(GenieMoodContext);
  if (context === undefined) {
    throw new Error('useGenieMood must be used within a GenieMoodProvider');
  }
  return context;
}

// Safe version that returns defaults if outside provider
export function useGenieMoodSafe() {
  const context = useContext(GenieMoodContext);
  if (context === undefined) {
    return {
      currentMood: 'default' as GenieMoodType,
      moodConfig: MOOD_CONFIGS.default,
      setMoodOverride: () => {},
      moodOverride: null,
      isAutoDetected: true,
    };
  }
  return context;
}
